// 입력칸. 글은 여기 안에 두고, **보내기는 밖에서 받은 함수**가 한다.
//
// ## 커맨드는 보내지 않는다
// `/이름 …` 은 메시지가 아니라 호출이다. 아는 이름일 때만 그 길로 가고, 모르는
// 이름이면 그냥 글로 보낸다 — 오타 하나로 사람이 쓴 문장이 사라지면 안 된다.

import {
  apply_format,
  type Edit,
  type Format,
  format_of_key,
  formats_at,
  on_enter,
} from '@maru/protocol'
import {
  Bold,
  ChartBar,
  Clock,
  Code,
  File,
  Italic,
  Link2,
  List,
  ListOrdered,
  Mic,
  Paperclip,
  SendHorizontal,
  SquareCode,
  Strikethrough,
  TextQuote,
  Video,
  X,
  Zap,
} from 'lucide-react'
import {
  lazy,
  type ReactNode,
  Suspense,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { type EmojiLook, emoji_table, hear_emoji, want_emoji } from './body_view.tsx'
import type { StartClip, Transcribe } from './clip.ts'
import { Mirror } from './composer_mirror.tsx'
import { why } from './confirm.tsx'
import { fmt_bytes, fmt_when_in } from './format.ts'
import { msg, useT } from './i18n.ts'
import { IconButton } from './kit/button.tsx'
import { Notice } from './kit/notice.tsx'
import { useToolbar } from './kit/toolbar.ts'
import { apply_mention, type Candidate } from './mention_menu.tsx'
import type { PollDraftLook } from './poll_form.tsx'
import type { ShortcutItem } from './shortcut_menu.tsx'
import { command_of, suggest } from './suggest.ts'
import { remedy } from './trouble.ts'

// `@`·`/` 메뉴는 처음 칠 때 받는다 — 줄 머리 아이콘·아바타를 첫 화면에 싣지 않는다.
const MentionMenu = lazy(() =>
  import('./mention_list.tsx').then((m) => ({ default: m.MentionMenu })),
)
// 클립 녹음기는 녹음기가 있는 기기에서만 뜬다 — 녹음 판·시계를 첫 화면에 싣지 않는다.
// 받는 동안은 잠긴 🎙·📹 가 자리를 지킨다(단추 줄이 뒤늦게 밀리지 않게).
const ClipRecorder = lazy(() =>
  import('./clip_recorder.tsx').then((m) => ({ default: m.ClipRecorder })),
)
// 예약 시간 고르기는 시계를 누를 때 받는다.
/** 예약 확인 귀띔이 떠 있는 시간. 읽을 틈은 주고, 판에서 그 예약을 만질 즈음엔 걷힌다. */
const WHISPER_FADE_MS = 8000
const WhenPicker = lazy(() => import('./when_picker.tsx').then((m) => ({ default: m.WhenPicker })))
const LinkForm = lazy(() => import('./link_form.tsx').then((m) => ({ default: m.LinkForm })))
// 앱 단축 메뉴는 단축을 등록한 앱이 있을 때만 뜬다 — 메뉴 키보드·자리 잡기를 첫 화면에 싣지 않는다.
const ShortcutMenu = lazy(() =>
  import('./shortcut_menu.tsx').then((m) => ({ default: m.ShortcutMenu })),
)
// 이모지 창은 반응 고르기와 같은 청크다 — 누를 때 받는다.
// 투표 만들기 창도 열 때만 받는다.
const PollDialog = lazy(() => import('./poll_form.tsx').then((m) => ({ default: m.PollDialog })))
const InsertPick = lazy(() => import('./emoji_pick.tsx').then((m) => ({ default: m.InsertPick })))

/** 올라간 첨부. 보내기 전까지 입력칸 아래 칩으로 보인다. */
export interface Attached {
  id: string
  name: string
  /** 이 기기에서만 쓰는 그림 미리보기(`blob:` 주소). 서버로 가지 않는다(3차 F163). */
  thumb?: string
  /** 올린 크기. 칩에 적는다. */
  bytes?: number
}

/** 칩의 미리보기 주소를 놓는다 — 쥔 채로 두면 그림 바이트가 창을 닫을 때까지 남는다. */
function let_go(list: readonly Attached[]): void {
  for (const a of list) if (a.thumb !== undefined) URL.revokeObjectURL(a.thumb)
}

/** 커맨드가 돌려준 것. `/remind` 는 `remind_at`(RFC 3339)을, `/dnd` 는 바뀐 알림 멈춤을 싣는다. */
export interface CommandReply {
  text: string
  remind_at?: string | undefined
  snooze?: { until: string | null } | undefined
}

/** 커맨드가 실패했다 — 글은 이미 사람의 말이다(`/이름 — 까닭`). */
class CommandFailed extends Error {}

/**
 * 보내기·예약 실패의 까닭. 고칠 수 있는 실패(너무 김·못 읽은 때·공지 채널)는 서버가 말한 까닭을,
 * 잠깐의 고장·모르는 실패는 `fallback`("잠시 후 다시")을 쓴다 — 전부 "다시 시도" 면 400 을 받은
 * 사람이 같은 글을 계속 다시 보낸다(3차 F148).
 */
function failed_why(e: unknown, fallback: string): string {
  const api = e instanceof Error && e.name === 'ApiError'
  return api && remedy(e) === null ? why(e) : fallback
}

export interface ComposerProps {
  /** 안내 문구에 쓴다. */
  channel_name: string
  /** 자리표시를 통째로 바꾼다(번역된 글자). 스레드처럼 채널 이름이 어울리지 않는 자리에서 쓴다. */
  placeholder?: string | undefined
  /** 성공하면 입력칸을 비운다. 던지면 글을 **그대로 두고** 오류를 보인다. */
  send(written: string, attachments: readonly Attached[]): Promise<void>
  /** 파일을 올린다(있을 때만 단추가 생긴다). 돌아온 것이 칩이 된다. */
  attach?(file: File): Promise<Attached>
  /** 클립을 녹음한다. `attach` 와 함께 있을 때만 🎙·📹 단추가 생긴다. */
  record?: StartClip | undefined
  /** 음성 클립 받아쓰기. `null` 이면 서버에 공급자가 없다는 안내만 선다. */
  transcribe?: Transcribe | undefined
  /** 타자 중임을 알린다(있을 때만). 빈도 조절은 부르는 쪽이 한다. */
  typing?(): void
  disabled?: boolean
  /** `@` 자동완성에 보일 사람들. 없으면 자동완성도 없다. */
  people?: readonly Candidate[]
  /** `/` 자동완성에 보일 커맨드들. `run` 과 함께 있을 때만 뜻이 있다. */
  commands?: readonly Candidate[]
  /**
   * 커맨드 명단을 처음 쓸 때 받는다. `commands` 가 아직 없을 때 글이 `/` 로 시작하면 부르고,
   * 보낼 때 명단이 아직이면 기다린다 — 안 기다리면 `/remind …` 가 그냥 글로 올라간다.
   */
  load_commands?(): Promise<readonly Candidate[]>
  /**
   * 커맨드를 부른다. 돌아온 글은 **나만 보는 귀띔**이다. `remind_at` 이 있으면(`/remind`) 글 대신
   * 그 때를 이 기기의 시간대로 적는다 — 서버가 적으면 UTC 가 사람에게 간다(3차 F147).
   */
  run?(name: string, text: string): Promise<CommandReply>
  /**
   * 쓰다 만 글. 처음 값으로만 쓴다 — 채널이 바뀌면 부르는 쪽이 `key` 로 갈아 끼운다. 읽는 손으로 주면 칸이
   * 설 때 한 번 부른다(스레드 칸 — 값으로 실으면 컴파일러가 메모한 옛 글이 돌아왔다, 3차 F167).
   */
  draft?: string | (() => string)
  /** 글이 바뀔 때마다 알린다. 보내고 나면 빈 글로 한 번 더 부른다. */
  on_draft?(text: string): void
  /**
   * 나중에 보낸다. 없으면 예약 단추도 없다.
   *
   * `due_at` 은 **절대 시각**이다 — 달력은 이 기기에 있고, 여기서 바꿔 넘겨야
   * "내일 아침 9시" 가 이 사람의 아침이 된다.
   */
  schedule?(written: string, attachments: readonly Attached[], due_at: string): Promise<void>
  /** 빈 입력칸에서 ↑. 고칠 글이 있어 열었으면 `true`. 슬랙과 같다. */
  edit_last?(): boolean
  /** 서식 줄 끝의 ⚡ 메뉴에 뜰 전역 단축들. `on_shortcut` 과 함께. */
  shortcuts?: readonly ShortcutItem[]
  on_shortcut?(key: string): Promise<void>
  /** 이모지 창에 커스텀 이모지를 그린다. */
  emoji_of?: EmojiLook | undefined
  emoji_names?: readonly string[] | undefined
  /** 투표를 올린다(질문을 글로 보내고 그 글에 투표를 단다). 없으면 투표 단추도 없다. */
  poll?(draft: PollDraftLook): Promise<void>
  /** 서자마자 초점 — 채널을 열었으면 곧 쓴다(슬랙과 같다). */
  autoFocus?: boolean
}

/**
 * 때 고르기. 브라우저의 `datetime-local` 은 **이 기기 시간대의 글자**를 준다 —
 * `new Date(value)` 가 그 시간대로 읽어 주므로 그대로 절대 시각이 된다.
 */
export function iso_of(local: string): string | null {
  const when = new Date(local)
  return Number.isNaN(when.getTime()) ? null : when.toISOString()
}

/** 서식 단추. 순서와 단축키 표기는 슬랙 도움말 201374536 "Format messages" 를 따른다. */
const TOOLS: readonly { f: Format; label: string; face: ReactNode; keys: string }[] = [
  { f: 'bold', label: msg('굵게'), face: <Bold />, keys: '⌘B' },
  { f: 'italic', label: msg('기울임'), face: <Italic />, keys: '⌘I' },
  { f: 'strike', label: msg('취소선'), face: <Strikethrough />, keys: '⇧⌘X' },
  { f: 'code', label: msg('코드'), face: <Code />, keys: '⇧⌘C' },
  { f: 'quote', label: msg('인용'), face: <TextQuote />, keys: '⇧⌘9' },
  { f: 'bullet', label: msg('글머리 기호 목록'), face: <List />, keys: '⇧⌘8' },
  { f: 'numbered', label: msg('번호 매기기 목록'), face: <ListOrdered />, keys: '⇧⌘7' },
  { f: 'code_block', label: msg('코드 블록'), face: <SquareCode />, keys: '⌥⇧⌘C' },
]

/** 서식 줄에서 무리를 가르는 자리(굵게·기울임·취소선 | 코드 | 인용·목록 | 코드 블록). */
const GROUP_ENDS: ReadonlySet<Format> = new Set<Format>(['strike', 'code', 'numbered'])

export function Composer(props: ComposerProps) {
  const t = useT()
  const bar = useToolbar<HTMLDivElement>()
  const { channel_name, send, attach, typing, disabled, people, commands, run, on_draft } = props
  const schedule = props.schedule
  const [written, set_written] = useState(() =>
    typeof props.draft === 'function' ? props.draft() : (props.draft ?? ''),
  )
  const [sending, set_sending] = useState(false)
  const [trouble, set_trouble] = useState<string | null>(null)
  const [whisper, set_whisper] = useState<string | null>(null)
  const [attached, set_attached] = useState<readonly Attached[]>([])
  const [uploading, set_uploading] = useState(0)
  const [caret, set_caret] = useState(0)
  const [picking, set_picking] = useState(false)
  const [polling, set_polling] = useState(false)
  // 링크를 넣을 자리 — 판을 여는 순간의 선택. 판에 초점이 가면 입력칸의 선택은 읽을 수 없다.
  const [linking, set_linking] = useState<{ start: number; end: number } | null>(null)
  const [chosen, set_chosen] = useState(0)
  const picker = useRef<HTMLInputElement>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  const placed = useRef(false)
  const mirror = useRef<HTMLDivElement>(null)
  const menu_id = useId()

  const has_something = written.trim().length > 0 || attached.length > 0
  const ready = has_something && !sending && uploading === 0 && !disabled
  // `:` 자동완성(3차 F136) — 조직의 커스텀 이모지가 먼저, 그다음 표준 이름 표(늦은 청크, `:` 를 치면 받는다).
  const table = useSyncExternalStore(hear_emoji, emoji_table, () => undefined)
  const emoji: Candidate[] = [
    ...(props.emoji_names ?? []).map((n): Candidate => {
      const src = props.emoji_of?.(n)
      return src === undefined ? { id: `:${n}`, name: n } : { id: `:${n}`, name: n, src }
    }),
    ...[...(table ?? [])].map(([name, glyph]) => ({ id: name, name, glyph })),
  ]
  const sug = suggest(written, caret, people, commands, emoji)
  const menu = sug?.items ?? []

  function choose(c: Candidate) {
    if (sug === null) return
    const next = apply_mention(written, sug.query, c.name, sug.sigil)
    set_written(next.text)
    on_draft?.(next.text)
    set_caret(next.caret)
    set_chosen(0)
    // 갈아 끼운 뒤 커서를 이름 뒤에 둔다 — 브라우저는 값이 바뀌면 끝으로 보낸다.
    queueMicrotask(() => box.current?.setSelectionRange(next.caret, next.caret))
  }

  /** 서식·목록 잇기가 만든 글로 갈아 끼운다. 새 글을 알리고 선택을 되돌려 이어 칠 수 있게 한다. */
  function put(next: Edit) {
    set_written(next.text)
    on_draft?.(next.text)
    set_caret(next.start)
    queueMicrotask(() => {
      box.current?.focus()
      box.current?.setSelectionRange(next.start, next.end)
    })
  }

  /** 선택에 서식을 두른다. */
  function format(f: Format) {
    const el = box.current
    const start = el?.selectionStart ?? written.length
    const end = el?.selectionEnd ?? written.length
    put(apply_format({ text: written, start, end }, f))
  }

  /** 링크 판을 연다. 고른 글자가 있으면 그것이 링크의 글자다. */
  function open_link() {
    const el = box.current
    set_linking({
      start: el?.selectionStart ?? written.length,
      end: el?.selectionEnd ?? written.length,
    })
  }

  /** 링크 판이 준 표기로 고른 자리를 갈아 끼운다. */
  function put_link(markup: string) {
    const at = linking ?? { start: written.length, end: written.length }
    const text = `${written.slice(0, at.start)}${markup}${written.slice(at.end)}`
    const caret = at.start + markup.length
    set_written(text)
    on_draft?.(text)
    set_caret(caret)
    set_linking(null)
    queueMicrotask(() => {
      box.current?.focus()
      box.current?.setSelectionRange(caret, caret)
    })
  }

  /**
   * 커서 자리에 글자를 끼운다(이모지·`@`·`/`). 앞이 낱말이면 띄운다 — `@` 는 줄 처음이나 공백
   * 뒤에서만 메뉴를 연다(`mention_query`).
   */
  function insert(piece: string, spaced: boolean) {
    const el = box.current
    const start = el?.selectionStart ?? written.length
    const end = el?.selectionEnd ?? written.length
    const before = written.slice(0, start)
    const lead = spaced && before !== '' && !/\s$/.test(before) ? ' ' : ''
    const text = `${before}${lead}${piece}${written.slice(end)}`
    const at = start + lead.length + piece.length
    set_written(text)
    on_draft?.(text)
    set_caret(at)
    set_chosen(0)
    if (piece === '/' && commands === undefined) props.load_commands?.().catch(() => {})
    queueMicrotask(() => {
      box.current?.focus()
      box.current?.setSelectionRange(at, at)
    })
  }

  /** 메뉴가 떠 있으면 방향키·Enter·Tab 은 메뉴 것이다. 먹었으면 참. */
  function menu_key(key: string): boolean {
    if (menu.length === 0) return false
    if (key === 'ArrowDown') set_chosen((i) => (i + 1) % menu.length)
    else if (key === 'ArrowUp') set_chosen((i) => (i + menu.length - 1) % menu.length)
    else if (key === 'Enter' || key === 'Tab') choose(menu[chosen] ?? (menu[0] as Candidate))
    else if (key === 'Escape') set_caret(-1)
    else return false
    return true
  }

  async function take(files: FileList | File[] | null) {
    if (attach === undefined || files === null) return
    for (const f of files) {
      set_uploading((n) => n + 1)
      try {
        const one = await attach(f)
        const thumb = f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined
        set_attached((list) => [
          ...list,
          { ...one, bytes: f.size, ...(thumb === undefined ? {} : { thumb }) },
        ])
      } catch (e) {
        set_trouble(why(e))
      }
      set_uploading((n) => n - 1)
    }
  }

  /** 아는 커맨드면 부르고 참을 준다. 첨부가 붙어 있으면 커맨드가 아니다. */
  async function command_in(): Promise<{ name: string; text: string } | null> {
    if (run === undefined || attached.length > 0) return null
    return command_of(written, commands, props.load_commands)
  }

  async function go() {
    if (!ready) return
    set_sending(true)
    set_trouble(null)
    try {
      const cmd = await command_in()
      if (cmd !== null) {
        const said = await (run as NonNullable<typeof run>)(cmd.name, cmd.text).catch((e) => {
          // 무엇이 실패했는지부터 — "메시지를 보내지 못했어요" 는 커맨드에게 틀린 말이다(F148).
          throw new CommandFailed(`/${cmd.name} — ${why(e)}`)
        })
        const until = said.snooze?.until
        set_whisper(
          said.remind_at !== undefined
            ? t('{when}에 알려 드릴게요. 예약 목록에서 보거나 취소할 수 있어요.', {
                when: fmt_when_in(said.remind_at),
              })
            : until === undefined
              ? said.text
              : until === null
                ? t('알림을 다시 켰어요.')
                : t('{when}까지 알림을 멈췄어요.', { when: fmt_when_in(until) }),
        )
      } else await send(written, attached)
      // 성공했을 때만 비운다. 실패에도 비우면 사람이 쓴 글이 사라진다 —
      // 채팅에서 가장 용서받기 어려운 버그다.
      set_written('')
      set_caret(0)
      let_go(attached)
      set_attached([])
      if (on_draft !== undefined) on_draft('')
    } catch (e) {
      // 고칠 수 있는 실패(너무 김·못 읽은 때)는 까닭을 말한다. 잠깐의 고장만 "다시 시도" 다(F148).
      set_trouble(
        e instanceof CommandFailed
          ? e.message
          : failed_why(e, t('메시지를 보내지 못했어요. 잠시 후 다시 시도해 주세요.')),
      )
    }
    set_sending(false)
  }

  /** 예약으로 보낸다. 성공했을 때만 비운다 — 보내기와 같은 규칙이다. */
  async function book(due_at: string) {
    if (!ready || schedule === undefined) return
    set_sending(true)
    set_trouble(null)
    try {
      await schedule(written, attached, due_at)
      set_written('')
      set_caret(0)
      let_go(attached)
      set_attached([])
      set_picking(false)
      // 누른 고르개가 사라지고 시계 단추도 글이 비어 꺼진다 — 다음 글을 칠 입력칸으로(3차 F300).
      queueMicrotask(() => box.current?.focus())
      if (on_draft !== undefined) on_draft('')
      // 언제 나가는지를 말한다 — "예약했어요" 만으로는 바로 고르기가 무슨 때를 잡았는지 모른다.
      const booked = t('{when}에 보낼게요. 예약 목록에서 확인할 수 있어요.', {
        when: fmt_when_in(due_at),
      })
      set_whisper(booked)
      // 이 귀띔은 그 순간의 사실이라 저절로 걷힌다 — 예약 판에서 [지금 보내기]·삭제를 한 뒤에도
      // "7시 10분에 보낼게요" 가 남아 있었다(3차 F268). 그사이 다른 말로 바뀌었으면 그것은 두다.
      setTimeout(() => set_whisper((now) => (now === booked ? null : now)), WHISPER_FADE_MS)
    } catch (e) {
      set_trouble(failed_why(e, t('메시지를 예약하지 못했어요. 잠시 후 다시 시도해 주세요.')))
    }
    set_sending(false)
  }

  const open_menu = sug !== null && menu.length > 0
  // 커서 자리에 켜진 서식 — 서식 줄 단추가 눌린 채로 보인다(슬랙과 같다).
  const on = formats_at(written, caret)
  return (
    <form
      className="Composer"
      onSubmit={(e) => {
        e.preventDefault()
        void go()
      }}
    >
      {whisper !== null && (
        <p className="Composer-pop whisper" role="status">
          <span className="Composer-whisper">{whisper}</span>
          <IconButton
            size="sm"
            label={t('닫기')}
            icon={<X />}
            onClick={() => {
              set_whisper(null)
              box.current?.focus()
            }}
          />
        </p>
      )}
      {picking && schedule !== undefined && (
        <Suspense fallback={null}>
          <WhenPicker
            pick={(due_at) => void book(due_at)}
            close={() => {
              set_picking(false)
              // 닫기(✕)가 고르개째 사라진다 — 연 시계 단추로 돌려준다(3차 F300).
              box.current?.closest('form')?.querySelector<HTMLElement>('.later')?.focus()
            }}
          />
        </Suspense>
      )}
      {sug !== null && (
        <Suspense fallback={null}>
          <MentionMenu
            id={menu_id}
            items={menu}
            chosen={chosen}
            choose={choose}
            sigil={sug.sigil}
          />
        </Suspense>
      )}
      {linking !== null && (
        <Suspense fallback={null}>
          <LinkForm
            text={written.slice(linking.start, linking.end)}
            put={put_link}
            close={() => {
              set_linking(null)
              box.current?.focus()
            }}
          />
        </Suspense>
      )}
      <div className="Composer-box" data-disabled={disabled || undefined}>
        <div className="Composer-tools format bar" role="toolbar" aria-label={t('서식')} {...bar}>
          {TOOLS.map((tool) => (
            <IconButton
              key={tool.f}
              size="sm"
              label={t(tool.label)}
              shortcut={tool.keys}
              icon={tool.face}
              data-end={GROUP_ENDS.has(tool.f) || undefined}
              pressed={on.includes(tool.f)}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => format(tool.f)}
            />
          ))}
          <IconButton
            size="sm"
            label={t('링크')}
            shortcut="⇧⌘U"
            icon={<Link2 />}
            disabled={disabled}
            aria-expanded={linking !== null}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (linking === null ? open_link() : set_linking(null))}
          />
        </div>
        {attached.length > 0 && (
          <ul className="attached" aria-label={t('첨부한 파일')}>
            {attached.map((a) => (
              <li key={a.id} className="Composer-chip">
                {a.thumb === undefined ? (
                  <File size={14} aria-hidden />
                ) : (
                  <img className="thumb" src={a.thumb} alt="" />
                )}
                <span className="Composer-chip-name">{a.name}</span>
                {a.bytes !== undefined && (
                  <span className="Composer-chip-size">{fmt_bytes(a.bytes)}</span>
                )}
                <IconButton
                  size="sm"
                  label={t('{name} 제거', { name: a.name })}
                  icon={<X />}
                  onClick={() => {
                    let_go([a])
                    set_attached((list) => list.filter((x) => x.id !== a.id))
                  }}
                />
              </li>
            ))}
          </ul>
        )}
        <div className="Composer-field">
          <Mirror text={written} at={mirror} />
          <textarea
            ref={box}
            className="Composer-input"
            // biome-ignore lint/a11y/noAutofocus: 채널을 열면 곧 쓴다 — 부르는 쪽이 좁은 화면(자판)을 뺀다
            autoFocus={props.autoFocus}
            // 창이 닫혔는데 돌아갈 자리가 없으면 여기로(kit `Dialog`). 처음 초점을 안 받는 자리(좁은 화면)는 뺀다.
            data-home={props.autoFocus === true ? '' : undefined}
            // 되살린 초안은 커서가 맨 앞에 서서, 이어 친 글이 초안 앞에 붙었다(3차 F271). 처음 초점을
            // 받을 때 한 번만 끝으로 민다 — 마우스로 누른 자리는 그 뒤에 브라우저가 다시 놓는다.
            onFocus={(e) => {
              if (placed.current) return
              placed.current = true
              const el = e.currentTarget
              if (el.selectionStart === 0 && el.selectionEnd === 0 && el.value !== '')
                el.setSelectionRange(el.value.length, el.value.length)
            }}
            rows={1}
            value={written}
            placeholder={props.placeholder ?? t('{name}에 메시지 보내기', { name: channel_name })}
            aria-label={props.placeholder ?? t('{name}에 메시지 보내기', { name: channel_name })}
            aria-controls={open_menu ? menu_id : undefined}
            aria-activedescendant={open_menu ? `${menu_id}-${chosen}` : undefined}
            aria-autocomplete={people !== undefined || commands !== undefined ? 'list' : undefined}
            disabled={disabled}
            onChange={(e) => {
              set_written(e.target.value)
              on_draft?.(e.target.value)
              if (commands === undefined && e.target.value.startsWith('/'))
                props.load_commands?.().catch(() => {})
              if (e.target.value.includes(':')) want_emoji()
              set_caret(e.target.selectionStart)
              set_chosen(0)
              typing?.()
            }}
            onSelect={(e) => set_caret(e.currentTarget.selectionStart)}
            onKeyDown={(e) => {
              // 조합 중(한글 입력기)에는 손대지 않는다 — 안 그러면 글자를 맺는 Enter 가
              // 문장을 날려 보낸다. 메뉴가 떠 있으면 키는 메뉴 것이다.
              if (e.nativeEvent.isComposing) return
              if (menu_key(e.key)) {
                e.preventDefault()
                return
              }
              // ⌘⇧U 는 링크 — 판이 필요해 서식 표(`format_of_key`)에 없다.
              if ((e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.code === 'KeyU') {
                e.preventDefault()
                open_link()
                return
              }
              const f = format_of_key({
                key: e.key,
                code: e.code,
                mod: e.metaKey || e.ctrlKey,
                shift: e.shiftKey,
                alt: e.altKey,
              })
              if (f !== null) {
                e.preventDefault()
                format(f)
                return
              }
              if (
                e.key === 'ArrowUp' &&
                written === '' &&
                !e.shiftKey &&
                !e.altKey &&
                props.edit_last?.() === true
              ) {
                e.preventDefault()
                return
              }
              // Enter 는 보내기, Shift+Enter 는 줄바꿈, ⌘Enter 는 어디서든 보내기. 목록 줄이면
              // 다음 항목을 잇고, 닫지 않은 코드 블록 안이면 줄을 바꾼다(`on_enter`).
              if (e.key === 'Enter') {
                const el = e.currentTarget
                const next = on_enter(
                  { text: written, start: el.selectionStart, end: el.selectionEnd },
                  { shift: e.shiftKey, mod: e.metaKey || e.ctrlKey, alt: e.altKey },
                )
                if (next === null) return
                e.preventDefault()
                if (next === 'send') void go()
                else put(next)
              }
            }}
            onPaste={(e) => {
              // 붙여 넣은 그림은 파일이다. 글이 아니라면 올린다.
              const files = [...e.clipboardData.files]
              if (files.length > 0) {
                e.preventDefault()
                void take(files)
              }
            }}
            onDrop={(e) => {
              e.preventDefault()
              void take(e.dataTransfer.files)
            }}
            onDragOver={(e) => e.preventDefault()}
            onScroll={(e) => {
              if (mirror.current !== null) mirror.current.scrollTop = e.currentTarget.scrollTop
            }}
          />
        </div>
        <div className="Composer-foot">
          {attach !== undefined && (
            <>
              <input
                ref={picker}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  void take(e.target.files)
                  e.target.value = ''
                }}
              />
              <IconButton
                size="sm"
                label={t('파일 첨부')}
                icon={<Paperclip />}
                disabled={disabled}
                onClick={() => picker.current?.click()}
              />
              {props.record !== undefined && (
                <Suspense
                  fallback={
                    <>
                      <IconButton size="sm" label={t('음성 클립 녹음')} icon={<Mic />} disabled />
                      <IconButton size="sm" label={t('영상 클립 녹화')} icon={<Video />} disabled />
                    </>
                  }
                >
                  <ClipRecorder
                    record={props.record}
                    take={(file) => take([file])}
                    transcribe={props.transcribe}
                    dictate={(text) => insert(text, true)}
                    disabled={disabled}
                    trouble={set_trouble}
                  />
                </Suspense>
              )}
            </>
          )}
          {people !== undefined && (
            <IconButton
              size="sm"
              label={t('사람 부르기')}
              shortcut="@"
              icon={<span className="glyph">@</span>}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insert('@', true)}
            />
          )}
          {run !== undefined && (
            <IconButton
              size="sm"
              label={written === '' ? t('커맨드') : t('커맨드는 빈 칸에서 시작해요')}
              shortcut="/"
              icon={<span className="glyph">/</span>}
              disabled={disabled || written !== ''}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insert('/', false)}
            />
          )}
          {props.poll !== undefined && (
            <IconButton
              size="sm"
              label={t('투표 만들기')}
              icon={<ChartBar />}
              disabled={disabled}
              onClick={() => set_polling(true)}
            />
          )}
          {polling && props.poll !== undefined && (
            <Suspense fallback={null}>
              <PollDialog make={props.poll} close={() => set_polling(false)} />
            </Suspense>
          )}
          {/* 늦게 오므로 끝에 둔다 — 앞 단추들이 밀리지 않게. */}
          <Suspense fallback={null}>
            <InsertPick
              on_pick={(e) => insert(e, e.startsWith(':'))}
              emoji_of={props.emoji_of}
              emoji_names={props.emoji_names}
              disabled={disabled}
            />
          </Suspense>
          {props.shortcuts !== undefined &&
            props.shortcuts.length > 0 &&
            props.on_shortcut !== undefined && (
              <Suspense
                fallback={<IconButton size="sm" label={t('앱 단축키')} icon={<Zap />} disabled />}
              >
                <ShortcutMenu
                  items={props.shortcuts}
                  label={t('앱 단축키')}
                  pick={(it) => props.on_shortcut?.(it.key) ?? Promise.resolve()}
                />
              </Suspense>
            )}
          <span className="Composer-send">
            {schedule !== undefined && (
              <IconButton
                size="sm"
                className="later"
                label={t('메시지 예약')}
                icon={<Clock />}
                aria-expanded={picking}
                disabled={!ready}
                onClick={() => set_picking((on) => !on)}
              />
            )}
            <IconButton
              type="submit"
              size="sm"
              variant="primary"
              label={uploading > 0 ? t('업로드 중') : t('보내기')}
              shortcut="Enter"
              icon={<SendHorizontal />}
              loading={uploading > 0 || sending}
              disabled={!ready}
            />
          </span>
        </div>
      </div>
      {trouble !== null && (
        <Notice plain className="Composer-error">
          {trouble}
        </Notice>
      )}
    </form>
  )
}
